// 清單邏輯（純函式）：由快照列建立項目、篩選、排序、分組。與 React 無關，方便測試。
import { GENRES, intersects, PLATFORMS, toMask } from '@dayone/core/dictionaries';
import { hasUnreleasedPlatform, releaseForView } from '@dayone/core/platform-release';
import {
  compareReleaseDate,
  GameStatus,
  isLongOverdue,
  isPossiblyStale,
  isReleased,
  PRECISION_RANK,
  parseReleaseDate,
  type ReleaseDate,
  releaseRange,
  releaseSortKey,
} from '@dayone/core/release-date';
import type { IndexRow, Manifest } from '@dayone/core/snapshot';
import { buildSearchText, matchesQuery } from '@dayone/core/text';
import { epochDay } from '@dayone/core/time';
import type { SortKey } from '../storage/prefs.ts';
import type { FilterState } from './state.ts';
import { type Section, sectionOf } from './timeline.ts';

export interface CatalogItem {
  row: IndexRow;
  date: ReleaseDate;
  /** 由日期計算的排序鍵（不依賴快照中的 k，避免兩者不一致） */
  sortKey: number;
  released: boolean;
  /** 今天發售：同時出現在「未發售」（顯示「今天發售」）與「已發售」 */
  releaseDay: boolean;
  stale: boolean;
  /** 模糊日期過期很久（上一季之前就結束）：不放在主要時間軸，列在最後的「可能過期」（GDO-23） */
  overdue: boolean;
  /** 計算 released 等欄位時的「今天」（依篩選換成其他平台的日期時重新計算用） */
  today: number;
  /** 預先算好的搜尋字串（載入時算一次，篩選時只比對） */
  search: string;
  /** 期待度百分位 0–100（只有未發售且有期待數的遊戲） */
  hypePct?: number;
}

/** 依日期算出清單需要的欄位 */
function dated(row: IndexRow, date: ReleaseDate, today: number) {
  const status = row.st as GameStatus;
  return {
    date,
    sortKey: releaseSortKey(date),
    released: isReleased(date, status, today),
    releaseDay: date.p === 'day' && releaseSortKey(date) === today,
    stale: isPossiblyStale(date, status, today),
    overdue: isLongOverdue(date, status, today),
  };
}

export function buildItems(rows: readonly IndexRow[], today: number): CatalogItem[] {
  const seen = new Set<string>();
  const items: CatalogItem[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    const date = parseReleaseDate(row.d);
    if (!date) continue; // 格式不符的列略過，不讓整份清單壞掉
    seen.add(row.id);
    // 版本名稱也可以搜尋（例如「townfall deluxe」找到 Silent Hill: Townfall，GDO-30）
    const editions = row.ed?.map(([title]) => `${row.n} ${title}`) ?? [];
    items.push({ row, ...dated(row, date, today), today, search: buildSearchText(row.n, row.z, ...editions) });
  }
  assignHypePercentiles(items);
  return items;
}

/** 還有平台沒發售（主日期已過、新平台版還沒推出的也算）：期待度百分位的比較對象 */
const anyUpcoming = (i: CatalogItem) => !i.released || (!!i.row.pd && hasUnreleasedPlatform(i.row, i.today));

/** 百分位：在「未發售且有期待數」的集合中，期待數勝過幾 % 的遊戲 */
export function assignHypePercentiles(items: CatalogItem[]): void {
  const pool = items.filter((i) => anyUpcoming(i) && i.row.hy !== undefined && i.row.hy > 0);
  const sorted = pool.map((i) => i.row.hy as number).sort((a, b) => a - b);
  for (const item of pool) {
    const hy = item.row.hy as number;
    // 嚴格小於的數量，同分者百分位相同
    let lo = 0;
    let hi = sorted.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((sorted[mid] as number) < hy) lo = mid + 1;
      else hi = mid;
    }
    item.hypePct = sorted.length > 1 ? Math.round((lo / (sorted.length - 1)) * 100) : 100;
  }
}

function monthStart(ym: string): number {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return epochDay(y, m, 1);
}
function monthEnd(ym: string): number {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return epochDay(m === 12 ? y + 1 : y, m === 12 ? 1 : m + 1, 1) - 1;
}

export type FilterCriteria = Pick<FilterState, 'status' | 'platforms' | 'q' | 'from' | 'to'> &
  Partial<Pick<FilterState, 'genres'>>;

/** 換成其他日期的項目快取：同一個日期回傳同一個物件，清單重新篩選時不會讓畫面以為每一列都變了 */
const redated = new WeakMap<CatalogItem, Map<string, CatalogItem>>();

/**
 * 各平台日期不同的遊戲（GDO-23）：依選的平台與要看的狀態換成對應的日期，例如選 Switch 2 時顯示 Switch 2 版的日期、
 * 看未發售時顯示還沒推出的平台版本。回傳 null 表示在這個檢視下不顯示。
 */
function forView(item: CatalogItem, platforms: number[] | null, view: { up: boolean; rel: boolean }) {
  if (!item.row.pd) return item;
  const date = releaseForView(item.row, platforms, view, item.today);
  if (!date) return null;
  if (compareReleaseDate(date, item.date) === 0 && date.p === item.date.p) return item;
  const key = JSON.stringify(date);
  let cache = redated.get(item);
  if (!cache) {
    cache = new Map();
    redated.set(item, cache);
  }
  let out = cache.get(key);
  if (!out) {
    out = { ...item, ...dated(item.row, date, item.today) };
    cache.set(key, out);
  }
  return out;
}

export function applyFilters(items: readonly CatalogItem[], s: FilterCriteria): CatalogItem[] {
  const wantUp = s.status.includes('up');
  const wantRel = s.status.includes('rel');
  const view = { up: wantUp, rel: wantRel };
  const platformIndexes = s.platforms.length
    ? s.platforms.map((k) => PLATFORMS.findIndex((p) => p.key === k)).filter((i) => i >= 0)
    : null;
  const mask = s.platforms.length ? toMask(PLATFORMS, s.platforms) : 0;
  const genreMask = s.genres?.length ? toMask(GENRES, s.genres) : 0;
  const hasRange = !!(s.from || s.to);
  const from = s.from ? monthStart(s.from) : Number.NEGATIVE_INFINITY;
  const to = s.to ? monthEnd(s.to) : Number.POSITIVE_INFINITY;
  const q = s.q.trim();

  const out: CatalogItem[] = [];
  for (const base of items) {
    const item = forView(base, platformIndexes, view);
    if (item && matches(item)) out.push(item);
  }
  return out;

  function matches(item: CatalogItem): boolean {
    if (item.row.st === GameStatus.cancelled) return false;
    // 今天發售的遊戲兩邊都顯示：發售當天是最常被查看的時候
    const visible = item.released ? wantRel || (wantUp && item.releaseDay) : wantUp;
    if (!visible) return false;
    if (mask && !intersects(item.row.p, mask)) return false;
    if (genreMask && !intersects(item.row.g, genreMask)) return false;
    if (hasRange) {
      // 不明確的日期：期間與範圍重疊就算符合；TBA 在有範圍時不顯示
      const range = releaseRange(item.date);
      if (!range || range.end < from || range.start > to) return false;
    }
    return !q || matchesQuery(item.search, q);
  }
}

const byDate = (a: CatalogItem, b: CatalogItem) =>
  compareReleaseDate(a.date, b.date) || a.row.n.localeCompare(b.row.n, 'en');

/** 依發售日排序時，過期很久的放在最後（自成「可能過期」一段） */
const overdueLast = (a: CatalogItem, b: CatalogItem) => Number(a.overdue) - Number(b.overdue);

export function sortItems(items: readonly CatalogItem[], sort: SortKey): CatalogItem[] {
  const list = [...items];
  switch (sort) {
    case 'date':
      return list.sort((a, b) => overdueLast(a, b) || byDate(a, b));
    case 'date-desc':
      // 新 → 舊，但 TBA 仍然排在最後（沒有日期可比）
      return list.sort((a, b) => {
        if (a.overdue !== b.overdue) return overdueLast(a, b);
        const aTba = a.date.p === 'tba';
        const bTba = b.date.p === 'tba';
        if (aTba !== bTba) return aTba ? 1 : -1;
        return (
          b.sortKey - a.sortKey ||
          PRECISION_RANK[a.date.p] - PRECISION_RANK[b.date.p] ||
          a.row.n.localeCompare(b.row.n, 'en')
        );
      });
    case 'hype':
      return list.sort((a, b) => (b.row.hy ?? -1) - (a.row.hy ?? -1) || byDate(a, b));
    case 'name':
      return list.sort((a, b) => a.row.n.localeCompare(b.row.n, 'en', { sensitivity: 'base' }));
  }
}

export interface SectionHeader {
  kind: 'header';
  key: string;
  section: Section;
  count: number;
  /** 段落中確定日期與只公布到月份的數量（月份段落的副標用） */
  exact: number;
  fuzzy: number;
}

export type ListEntry = SectionHeader | { kind: 'game'; key: string; item: CatalogItem; section: string };

/**
 * 依發售日排序時插入時間軸段落標題；其他排序方式不分段。
 * `today` 有值時（近到遠排序）前面會分出「本週／下週」。
 */
export function toEntries(items: readonly CatalogItem[], grouped: boolean, today: number | null = null): ListEntry[] {
  if (!grouped) return items.map((item) => ({ kind: 'game', key: item.row.id, item, section: '' }));
  const out: ListEntry[] = [];
  let header: SectionHeader | null = null;
  for (const item of items) {
    const { key, section } = sectionOf(item, today);
    if (!header || header.key !== key) {
      header = { kind: 'header', key, section, count: 0, exact: 0, fuzzy: 0 };
      out.push(header);
    }
    header.count++;
    if (item.date.p === 'day') header.exact++;
    else header.fuzzy++;
    out.push({ kind: 'game', key: item.row.id, item, section: key });
  }
  return out;
}

export const isGroupedSort = (sort: SortKey) => sort === 'date' || sort === 'date-desc';

/**
 * 需要載入哪些分片：未發售一定載入（今天剛發售、每日維護還沒搬移的遊戲也在裡面）；
 * 勾選已發售時載入今年與去年，日期範圍往前拉時載入更早的年份。
 */
export function neededShards(
  manifest: Manifest,
  s: Pick<FilterState, 'status' | 'from' | 'to'>,
  currentYear: number,
): string[] {
  const available = new Set(manifest.shards.map((x) => x.id));
  const out = ['upcoming'];
  if (s.status.includes('rel')) {
    const fromYear = s.from ? Number(s.from.slice(0, 4)) : currentYear - 1;
    const toYear = s.to ? Math.min(Number(s.to.slice(0, 4)), currentYear) : currentYear;
    for (let y = toYear; y >= Math.min(fromYear, toYear); y--) out.push(`released-${y}`);
    if (!s.from && !s.to) out.push('released-unknown');
  }
  return out.filter((id) => available.has(id));
}
